/**
 * The two section types that WRITE — the enquiry form and the newsletter sign-up.
 *
 * Both carry the honeypot from `ui.tsx`: a field hidden from people and filled in by
 * bots, which the server accepts with a 201 and silently drops. A CAPTCHA would be the
 * alternative, and it taxes every genuine visitor — disproportionately those using a
 * screen reader or a slow connection — to stop a problem this size.
 */

import { useState, type FormEvent } from 'react';
import { friendlyError } from '@serendib/shared';
import { api } from '../lib/api.js';
import { trackLead } from '../lib/tracking.js';
import { Field, Honeypot } from '../components/ui.jsx';
import { panelClass } from './frame.js';

type P = Record<string, any>;

export function ContactFormSection({ payload }: { payload: P }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState('sending');
    setError(null);
    setFieldErrors({});

    const form = new FormData(event.currentTarget);
    try {
      await api.post(
        '/public/leads',
        {
          name: String(form.get('name') ?? ''),
          email: String(form.get('email') ?? ''),
          phone: form.get('phone') ? String(form.get('phone')) : null,
          company: form.get('company') ? String(form.get('company')) : null,
          serviceInterest: form.get('serviceInterest') ? String(form.get('serviceInterest')) : null,
          budgetRange: form.get('budgetRange') ? String(form.get('budgetRange')) : null,
          message: String(form.get('message') ?? ''),
          source: payload.source ?? 'CONTACT',
          website: honeypot,
        },
        { auth: false },
      );
      // Lead fires only for a real enquiry, never for a newsletter signup: campaigns B
      // and C optimise toward this event, and diluting it with mailing-list additions
      // would teach Meta to find people who subscribe rather than people who buy.
      trackLead(payload.source ?? 'CONTACT');
      setState('sent');
    } catch (err) {
      setState('idle');
      setError(friendlyError(err));
      const errors = (err as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (errors) setFieldErrors(errors);
    }
  }

  if (state === 'sent') {
    return (
      <section className="section wrap">
        <div className={panelClass({ ground: payload.ground })} style={{ textAlign: 'center', display: 'grid', gap: 12, justifyItems: 'center' }}>
          <span
            aria-hidden="true"
            style={{ width: 48, height: 48, borderRadius: 999, background: 'var(--accent-wash)', color: 'var(--accent-deep)', display: 'grid', placeItems: 'center', fontSize: 22 }}
          >
            ✓
          </span>
          <h2 style={{ fontSize: 'var(--t-h3)' }}>Thank you</h2>
          <p className="lead measure">
            {payload.successMessage || 'We have your enquiry and will reply within one business day.'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="section wrap">
      <div className={panelClass({ ground: payload.ground })}>
        <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.3fr)', gap: 44, alignItems: 'start' }}>
          <div className="stack" style={{ gap: 12 }}>
            {payload.eyebrow ? <p className="eyebrow">{payload.eyebrow}</p> : null}
            {payload.heading ? <h2>{payload.heading}</h2> : null}
            {payload.body ? <p className="lead">{payload.body}</p> : null}
          </div>

          <form onSubmit={submit} className="stack" style={{ gap: 16 }} noValidate>
            <Honeypot value={honeypot} onChange={setHoneypot} />

            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <Field label="Your name" required error={fieldErrors.name}>
                <input className="input" name="name" required autoComplete="name" placeholder="e.g. Priyantha Perera" />
              </Field>
              <Field label="Email" required error={fieldErrors.email}>
                <input className="input" name="email" type="email" required autoComplete="email" placeholder="you@company.lk" />
              </Field>
            </div>

            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              {payload.showPhone !== false ? (
                <Field label="Telephone" error={fieldErrors.phone}>
                  <input className="input" name="phone" type="tel" autoComplete="tel" placeholder="011 330 40 48" />
                </Field>
              ) : null}
              {payload.showCompany !== false ? (
                <Field label="Business name" error={fieldErrors.company}>
                  <input className="input" name="company" autoComplete="organization" placeholder="e.g. Colombo Supermarkets" />
                </Field>
              ) : null}
            </div>

            {payload.serviceOptions?.length ? (
              <Field label="What is this about?" error={fieldErrors.serviceInterest}>
                <select className="select" name="serviceInterest" defaultValue="">
                  {/* "Any" as the no-selection option, never a blank line — a blank
                      select reads as a field that failed to load. */}
                  <option value="">Service · any</option>
                  {payload.serviceOptions.map((option: string) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </Field>
            ) : null}

            {payload.showBudget ? (
              <Field label="Rough budget" hint="A range is enough. It helps us propose something realistic." error={fieldErrors.budgetRange}>
                <select className="select" name="budgetRange" defaultValue="">
                  <option value="">Budget · not sure yet</option>
                  <option value="Under LKR 250,000">Under LKR 250,000</option>
                  <option value="LKR 250,000 – 1,000,000">LKR 250,000 – 1,000,000</option>
                  <option value="LKR 1,000,000 – 5,000,000">LKR 1,000,000 – 5,000,000</option>
                  <option value="Over LKR 5,000,000">Over LKR 5,000,000</option>
                </select>
              </Field>
            ) : null}

            <Field label="Operational details / brief requirement" required error={fieldErrors.message}>
              <textarea
                className="textarea"
                name="message"
                required
                rows={5}
                placeholder="We need a custom POS system for 3 checkout lanes connected to automatic thermal receipt printers…"
              />
            </Field>

            {error ? <p className="field-error" role="alert">{error}</p> : null}

            <div className="row">
              <button className="btn btn--primary" type="submit" disabled={state === 'sending'}>
                {state === 'sending' ? 'Sending…' : payload.submitLabel || 'Send enquiry'}
              </button>
              <p className="xs dim">We reply within one business day.</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

export function NewsletterSection({ payload }: { payload: P }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState('sending');
    setError(null);
    const form = new FormData(event.currentTarget);
    try {
      await api.post('/public/subscribe', { email: String(form.get('email') ?? ''), website: honeypot }, { auth: false });
      setState('sent');
    } catch (err) {
      setState('idle');
      setError(friendlyError(err));
    }
  }

  return (
    <section className="section wrap">
      <div className={`${panelClass({ ground: payload.ground })} spread`}>
        <div className="stack" style={{ gap: 8 }}>
          {payload.eyebrow ? <p className="eyebrow">{payload.eyebrow}</p> : null}
          <h2 style={{ fontSize: 'var(--t-h3)' }}>{payload.heading ?? 'Stay updated'}</h2>
          {payload.body ? <p className="small muted measure">{payload.body}</p> : null}
        </div>

        {state === 'sent' ? (
          <p className="field-ok" role="status">{payload.successMessage || 'Thank you — you are on the list.'}</p>
        ) : (
          <form onSubmit={submit} className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
            <Honeypot value={honeypot} onChange={setHoneypot} />
            <label className="sr-only" htmlFor="newsletter-email">Email address</label>
            <input
              id="newsletter-email"
              className="input"
              name="email"
              type="email"
              required
              placeholder={payload.placeholder ?? 'Enter your email'}
              style={{ minWidth: 220 }}
            />
            <button className="btn btn--primary" type="submit" disabled={state === 'sending'}>
              {state === 'sending' ? '…' : payload.submitLabel || 'Join'}
            </button>
          </form>
        )}
        {error ? <p className="field-error" role="alert">{error}</p> : null}
      </div>
    </section>
  );
}
